// =====================================================================
// Asset valuation rules (single source of truth, shared by Assets,
// Family Sharing, Insights, etc.)
// =====================================================================
//
//  * Only these categories track a unit/quantity:
//      stocks, gold, silver, crypto, collectibles
//  * gold & silver  -> `value` is the PER-UNIT price (₹/gram). The holding
//      total is quantity × value (the user knows the per-unit rate, not the
//      full value).
//  * stocks, crypto, collectibles -> `value` is the TOTAL value of the
//      holding. The quantity is only used to *divide* the value when a slice
//      is distributed to a goal / family member (never to multiply).
//  * everything else -> no quantity; `value` is the total.
//  * If the current value is blank/0 we fall back to the purchase value.
// =====================================================================

export const QUANTITY_CATEGORIES = [
  "stocks",
  "gold",
  "silver",
  "crypto",
  "collectibles",
];

// Categories where `value` is per-unit and the total = quantity × value.
export const PER_UNIT_CATEGORIES = ["gold", "silver"];

const num = (v) => Number(String(v ?? "").replace(/,/g, "")) || 0;

export const hasQuantity = (cat) => QUANTITY_CATEGORIES.includes(cat);
export const isPerUnit = (cat) => PER_UNIT_CATEGORIES.includes(cat);

// Effective base value: prefer the current value, fall back to purchase value
// when the current value is missing / zero.
export const baseValue = (a) => num(a?.value) || num(a?.purchase_value);

// Total current value of the whole holding.
export const assetTotal = (a) => {
  const base = baseValue(a);
  if (isPerUnit(a?.category) && num(a?.quantity) > 0) {
    return num(a.quantity) * base;
  }
  return base;
};

// Total amount invested (cost) for the whole holding.
export const assetInvested = (a) => {
  const pv = num(a?.purchase_value);
  if (!pv) return 0;
  if (isPerUnit(a?.category) && num(a?.quantity) > 0) {
    return num(a.quantity) * pv;
  }
  return pv;
};

// Rupee value represented by `units` of a quantity asset — used when sharing
// or inheriting a partial quantity.
//   gold/silver           -> per-unit value × units
//   stocks/crypto/collect. -> total value × (units / total units)
export const valueOfUnits = (a, units) => {
  const u = num(units);
  if (isPerUnit(a?.category)) return baseValue(a) * u;
  const tq = num(a?.quantity);
  return tq > 0 ? assetTotal(a) * (u / tq) : 0;
};
